import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from "react";

/**
 * Drag-to-resize state for the bottom-right-docked help panel (see {@link HelpPanel}).
 *
 * The panel is pinned to the bottom-right corner, so it grows by extending up and to the left —
 * the grip lives on the **top-left** corner and dragging away from the corner enlarges the panel.
 * The current fixed size is the **minimum**: the panel can only grow, never shrink below it. The
 * chosen size is persisted to `localStorage` so it survives reloads.
 */

const STORAGE_KEY = "helpPanel.size";

/** Today's fixed size — the floor the panel can never shrink below. */
export const MIN_WIDTH = 460;
export const MIN_HEIGHT = 600;

/** Matches the panel's `bottom-4`/`right-4` inset plus a 1rem margin on the opposite edge. */
const VIEWPORT_INSET = 32;

export interface PanelSize {
	width: number;
	height: number;
}

interface ResizeBounds {
	minWidth: number;
	minHeight: number;
	maxWidth: number;
	maxHeight: number;
}

const clamp = (value: number, min: number, max: number): number =>
	Math.min(Math.max(value, min), max);

/**
 * Size limits derived from the live viewport. The max keeps the panel inside the viewport; the min
 * shrinks below {@link MIN_WIDTH}/{@link MIN_HEIGHT} only on a viewport too small to fit them (the
 * CSS `max-w`/`max-h` caps handle the visual clamp there).
 */
const getBounds = (): ResizeBounds => {
	const maxWidth = window.innerWidth - VIEWPORT_INSET;
	const maxHeight = window.innerHeight - VIEWPORT_INSET;

	return {
		maxWidth,
		maxHeight,
		minWidth: Math.min(MIN_WIDTH, maxWidth),
		minHeight: Math.min(MIN_HEIGHT, maxHeight),
	};
};

const readInitialSize = (): PanelSize => {
	const bounds = getBounds();
	const fallback: PanelSize = { width: bounds.minWidth, height: bounds.minHeight };

	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);

		if (!raw) {
			return fallback;
		}

		const parsed = JSON.parse(raw) as Partial<PanelSize>;

		if (typeof parsed.width !== "number" || typeof parsed.height !== "number") {
			return fallback;
		}

		return {
			width: clamp(parsed.width, bounds.minWidth, bounds.maxWidth),
			height: clamp(parsed.height, bounds.minHeight, bounds.maxHeight),
		};
	} catch {
		return fallback;
	}
};

const persistSize = (size: PanelSize): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(size));
	} catch {
		// Ignore storage failures (private mode / quota) — the size just won't persist.
	}
};

interface UseHelpPanelResize {
	size: PanelSize;
	isResizing: boolean;
	onResizeStart: (event: ReactPointerEvent<HTMLElement>) => void;
}

export const useHelpPanelResize = (): UseHelpPanelResize => {
	const [size, setSize] = useState<PanelSize>(readInitialSize);
	const [isResizing, setIsResizing] = useState(false);

	// Mirror the latest size so `onPointerUp` can persist it without re-creating the drag handler.
	const sizeRef = useRef<PanelSize>(size);

	useEffect(() => {
		sizeRef.current = size;
	}, [size]);

	const onResizeStart = useCallback((event: ReactPointerEvent<HTMLElement>) => {
		event.preventDefault();
		const start = {
			x: event.clientX,
			y: event.clientY,
			width: sizeRef.current.width,
			height: sizeRef.current.height,
		};

		event.currentTarget.setPointerCapture?.(event.pointerId);
		setIsResizing(true);

		// Dragging up/left (smaller clientX/Y) increases the delta, so the panel grows.
		const onPointerMove = (moveEvent: PointerEvent) => {
			const bounds = getBounds();

			setSize({
				width: clamp(start.width + (start.x - moveEvent.clientX), bounds.minWidth, bounds.maxWidth),
				height: clamp(
					start.height + (start.y - moveEvent.clientY),
					bounds.minHeight,
					bounds.maxHeight
				),
			});
		};

		const onPointerUp = () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("pointerup", onPointerUp);
			setIsResizing(false);
			persistSize(sizeRef.current);
		};

		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
	}, []);

	return { size, isResizing, onResizeStart };
};
